import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {/* Camel Logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/yv3r7xql_Adobe%20Express%20-%20file.png"
                alt="Camel Logo"
                className="h-10 w-10 object-contain"
                style={{
                  filter: isScrolled 
                    ? 'brightness(0) saturate(100%) invert(29%) sepia(74%) saturate(1447%) hue-rotate(2deg) brightness(94%) contrast(97%)'
                    : 'brightness(0) invert(1) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                }}
              />
              {/* Camel Text */}
              <div 
                className={`text-3xl font-bold transition-colors ${
                  isScrolled ? 'text-amber-800' : 'text-white'
                }`}
                style={{ 
                  fontFamily: "'Monotype Corsiva', cursive", 
                  fontWeight: 'normal',
                  textShadow: isScrolled ? 'none' : '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                Camel
              </div>
            </div>
            <div 
              className={`hidden md:block text-sm transition-colors ${
                isScrolled ? 'text-amber-700' : 'text-white'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 3px rgba(0,0,0,0.5)'
              }}
            >
              Rubber Industries
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('home')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-700 hover:text-amber-700' 
                  : 'text-white hover:text-gray-200'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-700 hover:text-amber-700' 
                  : 'text-white hover:text-gray-200'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('benefits')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-700 hover:text-amber-700' 
                  : 'text-white hover:text-gray-200'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Benefits
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-700 hover:text-amber-700' 
                  : 'text-white hover:text-gray-200'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Pricing
            </button>
            <button 
              onClick={() => scrollToSection('testimonials')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-700 hover:text-amber-700' 
                  : 'text-white hover:text-gray-200'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Testimonials
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className={`transition-colors font-medium ${
                isScrolled 
                  ? 'text-gray-700 hover:text-amber-700' 
                  : 'text-white hover:text-gray-200'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Contact
            </button>
          </nav>

          {/* Contact Info */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="tel:+919824138001"
              className={`flex flex-col items-end transition-colors ${
                isScrolled 
                  ? 'text-amber-800 hover:text-amber-900' 
                  : 'text-white hover:text-gray-100'
              }`}
              style={{
                textShadow: isScrolled ? 'none' : '1px 1px 3px rgba(0,0,0,0.5)'
              }}
            >
              <span className="text-sm font-semibold">Hasmukh Patel</span>
              <div className="flex items-center space-x-1">
                <Phone size={14} />
                <span className="text-sm">+91 9824138001</span>
              </div>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden transition-colors ${
              isScrolled 
                ? 'text-gray-700 hover:text-amber-700' 
                : 'text-white hover:text-gray-200'
            }`}
            style={{
              filter: isScrolled ? 'none' : 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))'
            }}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-3 border-t border-amber-200 pt-4">
            <button 
              onClick={() => scrollToSection('home')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors rounded-lg font-medium"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors rounded-lg font-medium"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('benefits')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors rounded-lg font-medium"
            >
              Benefits
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors rounded-lg font-medium"
            >
              Pricing
            </button>
            <button 
              onClick={() => scrollToSection('testimonials')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors rounded-lg font-medium"
            >
              Testimonials
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors rounded-lg font-medium"
            >
              Contact
            </button>
            <div className="px-4 pt-3 border-t border-amber-200">
              <a 
                href="tel:+919824138001"
                className="flex flex-col text-amber-800 hover:text-amber-900 transition-colors"
              >
                <span className="text-sm font-semibold mb-1">Hasmukh Patel</span>
                <div className="flex items-center space-x-2">
                  <Phone size={16} />
                  <span className="text-sm">+91 9824138001</span>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
